import type { PermissionDefinition } from '../types/permission.js';
import { SystemPermissions } from './permissions.js';

const MB = 1024 * 1024;

export const UPLOAD_PURPOSE_PREFLIGHT_ASSET = 'PREFLIGHT_ASSET' as const;
export const UPLOAD_PURPOSE_SCRIPT_REVIEW = 'SCRIPT_REVIEW' as const;
/** A user-supplied script FILE (CSV/TXT, or an Excel file converted to CSV client-side). */
export const UPLOAD_PURPOSE_SCRIPT_REVIEW_SCRIPT = 'SCRIPT_REVIEW_SCRIPT' as const;
export const UPLOAD_PURPOSE_VENDOR_LOGO = 'VENDOR_LOGO' as const;
export const UPLOAD_PURPOSE_TENANT_LOGO = 'TENANT_LOGO' as const;
export const UPLOAD_PURPOSE_USER_AVATAR = 'USER_AVATAR' as const;
/** AI-generated persona avatar (A2) — written server-side by the personas worker via
 * `@nexus/image-gen`; no client presign/confirm flow exists for this purpose yet. */
export const UPLOAD_PURPOSE_PERSONA_AVATAR = 'PERSONA_AVATAR' as const;
/** One mp4 video reference for persona script generation (AD-2148). */
export const UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE = 'PERSONA_SCRIPT_REFERENCE' as const;
/** Guidelines PDF/DOCX/TXT persisted by extract, then sent as `guidelinesUploadId`. */
export const UPLOAD_PURPOSE_PERSONA_SCRIPT_GUIDELINES = 'PERSONA_SCRIPT_GUIDELINES' as const;

/**
 * `Cache-Control` written onto image objects that the API hands to the browser as a
 * signed URL. Without it GCS serves a non-public object as `private, max-age=0`, so
 * every fresh `<img>` re-downloads bytes the browser already has — visible as avatars
 * and logos reloading whenever a list refetches or a preview opens.
 *
 * `private` because signed URLs must never be stored by a shared cache. Deliberately not
 * `immutable`: a persona avatar's object key is keyed by persona, not by upload, so a
 * regenerated image reuses the key and still needs a revalidation path.
 */
export const UPLOAD_IMAGE_CACHE_CONTROL = 'private, max-age=86400' as const;

/** Purposes whose objects are images rendered in the app, so they get the header above. */
export const CACHEABLE_IMAGE_UPLOAD_PURPOSES = [
  UPLOAD_PURPOSE_VENDOR_LOGO,
  UPLOAD_PURPOSE_TENANT_LOGO,
  UPLOAD_PURPOSE_USER_AVATAR,
  UPLOAD_PURPOSE_PERSONA_AVATAR,
] as const;

export type UploadPurpose =
  | typeof UPLOAD_PURPOSE_PREFLIGHT_ASSET
  | typeof UPLOAD_PURPOSE_SCRIPT_REVIEW
  | typeof UPLOAD_PURPOSE_SCRIPT_REVIEW_SCRIPT
  | typeof UPLOAD_PURPOSE_VENDOR_LOGO
  | typeof UPLOAD_PURPOSE_TENANT_LOGO
  | typeof UPLOAD_PURPOSE_USER_AVATAR
  | typeof UPLOAD_PURPOSE_PERSONA_AVATAR
  | typeof UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE
  | typeof UPLOAD_PURPOSE_PERSONA_SCRIPT_GUIDELINES;

export interface UploadPolicy {
  /** GCS object key segment under the upload bucket (e.g. preflight/{name}-{tenantId}/batch_YYYY-MM-DD_{id}/file). */
  objectKeyPrefix: string;
  allowedContentTypes: readonly string[];
  maxSizeBytesByContentType: Readonly<Record<string, number>>;
  presignPermission: PermissionDefinition;
  confirmPermission: PermissionDefinition;
}

export const UPLOAD_POLICIES: Record<UploadPurpose, UploadPolicy> = {
  [UPLOAD_PURPOSE_PREFLIGHT_ASSET]: {
    objectKeyPrefix: 'preflight',
    allowedContentTypes: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'video/mp4',
      'video/quicktime',
    ],
    maxSizeBytesByContentType: {
      'image/jpeg': 25 * MB,
      'image/png': 25 * MB,
      'image/webp': 25 * MB,
      'video/mp4': 500 * MB,
      'video/quicktime': 500 * MB,
    },
    presignPermission: SystemPermissions.PREFLIGHT_UPLOAD_PRESIGN,
    confirmPermission: SystemPermissions.PREFLIGHT_UPLOAD_CONFIRM,
  },
  [UPLOAD_PURPOSE_SCRIPT_REVIEW]: {
    objectKeyPrefix: 'script-review',
    allowedContentTypes: ['video/mp4', 'video/quicktime'],
    maxSizeBytesByContentType: {
      'video/mp4': 1024 * MB,
      'video/quicktime': 1024 * MB,
    },
    presignPermission: SystemPermissions.SCRIPT_REVIEW_UPLOAD_PRESIGN,
    confirmPermission: SystemPermissions.SCRIPT_REVIEW_UPLOAD_CONFIRM,
  },
  [UPLOAD_PURPOSE_SCRIPT_REVIEW_SCRIPT]: {
    objectKeyPrefix: 'script-review/scripts',
    // Browser MIME for .csv varies (text/csv vs Excel-flavored); .txt is stable.
    //
    // PLAIN TEXT ONLY, deliberately — anything the worker can hand to Gemini as
    // text/plain and trust. Two formats are pointedly absent:
    //   - PDF: dropped, scene extraction from one proved unreliable. Reviews
    //     created before this still re-analyze fine (the worker keeps reading
    //     them); this list only governs NEW uploads.
    //   - Spreadsheets: a .xlsx is a ZIP of XML, so it would arrive as binary
    //     noise and quietly extract nonsense scenes. Excel is converted to CSV
    //     in the browser before it gets here — see
    //     frontend/web/src/features/script-review/lib/spreadsheet-to-csv.ts.
    // Don't re-add either without teaching the worker to parse it first.
    allowedContentTypes: ['text/csv', 'application/csv', 'application/vnd.ms-excel', 'text/plain'],
    maxSizeBytesByContentType: {
      'text/csv': 25 * MB,
      'application/csv': 25 * MB,
      'application/vnd.ms-excel': 25 * MB,
      'text/plain': 25 * MB,
    },
    // Reuse the script-review upload permissions (same feature, SUPER_TENANT-gated).
    presignPermission: SystemPermissions.SCRIPT_REVIEW_UPLOAD_PRESIGN,
    confirmPermission: SystemPermissions.SCRIPT_REVIEW_UPLOAD_CONFIRM,
  },
  [UPLOAD_PURPOSE_VENDOR_LOGO]: {
    objectKeyPrefix: 'marketplace/vendor-logos',
    allowedContentTypes: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/svg+xml',
    ],
    maxSizeBytesByContentType: {
      'image/jpeg': 2 * MB,
      'image/png': 2 * MB,
      'image/webp': 2 * MB,
      'image/svg+xml': 512 * 1024,
    },
    presignPermission: SystemPermissions.MARKETPLACE_ADMIN_UPLOAD_LOGO_PRESIGN,
    confirmPermission: SystemPermissions.MARKETPLACE_ADMIN_UPLOAD_LOGO_CONFIRM,
  },
  [UPLOAD_PURPOSE_TENANT_LOGO]: {
    objectKeyPrefix: 'identity-hub/tenant-logos',
    allowedContentTypes: [
      'image/jpeg',
      'image/png',
      'image/webp',
      'image/svg+xml',
    ],
    maxSizeBytesByContentType: {
      'image/jpeg': 2 * MB,
      'image/png': 2 * MB,
      'image/webp': 2 * MB,
      'image/svg+xml': 512 * 1024,
    },
    presignPermission: SystemPermissions.IDENTITY_HUB_UPLOAD_TENANT_LOGO_PRESIGN,
    confirmPermission: SystemPermissions.IDENTITY_HUB_UPLOAD_TENANT_LOGO_CONFIRM,
  },
  [UPLOAD_PURPOSE_USER_AVATAR]: {
    objectKeyPrefix: 'identity-hub/user-avatars',
    allowedContentTypes: [
      'image/jpeg',
      'image/png',
      'image/webp',
    ],
    maxSizeBytesByContentType: {
      'image/jpeg': 5 * MB,
      'image/png': 5 * MB,
      'image/webp': 5 * MB,
    },
    presignPermission: SystemPermissions.IDENTITY_HUB_UPLOAD_USER_AVATAR_PRESIGN,
    confirmPermission: SystemPermissions.IDENTITY_HUB_UPLOAD_USER_AVATAR_CONFIRM,
  },
  [UPLOAD_PURPOSE_PERSONA_AVATAR]: {
    objectKeyPrefix: 'personas/avatars',
    allowedContentTypes: [
      'image/jpeg',
      'image/png',
      'image/webp',
    ],
    maxSizeBytesByContentType: {
      'image/jpeg': 5 * MB,
      'image/png': 5 * MB,
      'image/webp': 5 * MB,
    },
    presignPermission: SystemPermissions.PERSONAS_UPLOAD_AVATAR_PRESIGN,
    confirmPermission: SystemPermissions.PERSONAS_UPLOAD_AVATAR_CONFIRM,
  },
  [UPLOAD_PURPOSE_PERSONA_SCRIPT_REFERENCE]: {
    objectKeyPrefix: 'persona-scripts/reference',
    allowedContentTypes: ['video/mp4'],
    maxSizeBytesByContentType: {
      'video/mp4': 250 * MB,
    },
    presignPermission: SystemPermissions.PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_PRESIGN,
    confirmPermission: SystemPermissions.PERSONA_OPPORTUNITY_SETS_REFERENCE_VIDEO_CONFIRM,
  },
  [UPLOAD_PURPOSE_PERSONA_SCRIPT_GUIDELINES]: {
    objectKeyPrefix: 'persona-scripts/guidelines',
    allowedContentTypes: [
      'text/plain',
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
    ],
    maxSizeBytesByContentType: {
      'text/plain': 5 * MB,
      'application/pdf': 5 * MB,
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 5 * MB,
    },
    presignPermission: SystemPermissions.PERSONA_OPPORTUNITY_SETS_GUIDELINES_EXTRACT,
    confirmPermission: SystemPermissions.PERSONA_OPPORTUNITY_SETS_GUIDELINES_EXTRACT,
  },
};

export function getUploadPolicy(purpose: string): UploadPolicy | undefined {
  return UPLOAD_POLICIES[purpose as UploadPurpose];
}

/**
 * Canonical file extension per allowed content type. Used to give mirrored URL assets a
 * DS-recognized extension so the analyzer classifies image vs video correctly — the DS
 * `/analyze` interface sniffs asset type from the URL extension, not from any mime field
 * (AD-1721). Keep in sync with every entry in UPLOAD_POLICIES.allowedContentTypes. Values
 * are the canonical extension only (never a source URL's raw extension, e.g. `.jfif`).
 */
export const MIME_TO_EXTENSION: Readonly<Record<string, string>> = {
  'image/jpeg': 'jpg',
  'image/png': 'png',
  'image/webp': 'webp',
  'image/svg+xml': 'svg',
  'video/mp4': 'mp4',
  'video/quicktime': 'mov',
  'text/plain': 'txt',
  'application/pdf': 'pdf',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'docx',
};

/**
 * Append the canonical extension for `contentType` to `objectKey`. Returns the key
 * unchanged when the content type is unknown or the key already ends with that canonical
 * extension (idempotent — safe to call twice on the same key).
 */
export function appendCanonicalExtension(objectKey: string, contentType: string): string {
  const ext = MIME_TO_EXTENSION[contentType];
  if (!ext) return objectKey;
  if (objectKey.toLowerCase().endsWith(`.${ext}`)) return objectKey;
  return `${objectKey}.${ext}`;
}

export interface UploadMetaInput {
  contentType: string;
  sizeBytes: number;
}

export type UploadValidationFailure =
  | 'DISALLOWED_CONTENT_TYPE'
  | 'SIZE_EXCEEDS_CAP';

export function validateUploadMeta(
  meta: UploadMetaInput,
  policy: UploadPolicy,
): { ok: true } | { ok: false; reason: UploadValidationFailure } {
  if (!policy.allowedContentTypes.includes(meta.contentType)) {
    return { ok: false, reason: 'DISALLOWED_CONTENT_TYPE' };
  }
  const cap = policy.maxSizeBytesByContentType[meta.contentType];
  if (cap == null || meta.sizeBytes > cap) {
    return { ok: false, reason: 'SIZE_EXCEEDS_CAP' };
  }
  return { ok: true };
}

/** Largest allowed object size for a purpose (e.g. 500 MiB video cap for Pre Flight). */
export function maxUploadBytesForPurpose(purpose: UploadPurpose): number {
  const policy = UPLOAD_POLICIES[purpose];
  return Math.max(...Object.values(policy.maxSizeBytesByContentType));
}
